<template>
	<div class="chart-range" ref="container">
		
		<div class="range-bar">
			<div 
				class="range-indicator" 
				:style="{ left: indicatorLeft + 'px', width: rangeWidth + 'px' }"
				@mousedown="startRangeDrag"
			></div>
			<div 
				class="handle start-handle" 
				:style="{ left: startHandleOffset + 'px' }"
				@mousedown="(e) => startDrag(e, 'start')"
			></div>
			<div 
				class="handle end-handle" 
				:style="{ left: endHandleOffset + 'px' }"
				@mousedown="(e) => startDrag(e, 'end')"
			></div>
		</div>
	</div>
</template>


<script>

const HANDLE_SIZE = 12;

export default {
	props: {
		// Initial values
		dataPresent: {
			type: Boolean,
			default: false
		},
		graphScale: {
			type: Number,
			default: 1.0 // 1.0 means full graph is visible
		},
		graphPosition: {
			type: Number,
			default: 0	// Changed from 0.5 to 0
		},
		minScale: {
			type: Number,
			default: 0
		}
	},
	data() {
		return {
			startHandlePos: this.graphPosition, // Internal representation as a fraction
			endHandlePos: this.graphPosition + this.graphScale, // Internal representation as a fraction
			scale: this.graphScale,
			position: this.graphPosition,
			isDraggingHandle: false,
			isDraggingRange: false,
			startDragX: 0,
			activeHandle: null, // 'start', 'end', or null
			containerWidth: 0,
			internalUpdate: false, // Flag to track internal updates
		};
	},
	computed: {
		trackWidth() {
			return Math.max(0, this.containerWidth - HANDLE_SIZE * 2);
		},
		startHandleOffset() {
			return this.startHandlePos * this.trackWidth;
		},
		endHandleOffset() {
			return HANDLE_SIZE * 2 + this.endHandlePos * this.trackWidth;
		},
		indicatorLeft() {
			return HANDLE_SIZE / 2 + this.startHandleOffset;
		},
		rangeWidth() {
			return HANDLE_SIZE + (this.endHandlePos - this.startHandlePos) * this.trackWidth;
		},
		effectiveMinScale() {
			return Math.min(1, Math.max(0, this.minScale));
		},
	},
	watch: {
		graphPosition(newPosition) {
			if (!this.internalUpdate) {
				this.position = newPosition;
				this.startHandlePos = newPosition;
				this.endHandlePos = newPosition + this.scale;
			}
		},
		graphScale(newScale) {
			if (!this.internalUpdate) {
				this.scale = newScale;
				this.endHandlePos = this.position + newScale;
			}
		}
	},
	methods: {
		startDrag(event, handle) {
			this.isDraggingHandle = true;
			this.activeHandle = handle;
			this.startDragX = event.clientX;
			
			// Add event listeners for drag and release
			window.addEventListener('mousemove', this.handleDrag);
			window.addEventListener('mouseup', this.endDrag);
		},
		
		startRangeDrag(event) {
			if (!this.isDraggingHandle) {
				this.isDraggingRange = true;
				this.startDragX = event.clientX;
				
				// Add event listeners for drag and release
				window.addEventListener('mousemove', this.handleRangeDrag);
				window.addEventListener('mouseup', this.endRangeDrag);
			}
		},
		
		handleDrag(event) {
			if (!this.isDraggingHandle) return;
			if (!this.trackWidth) return;
			
			const deltaX = event.clientX - this.startDragX;
			const deltaPercent = deltaX / this.trackWidth;
			this.startDragX = event.clientX;
			
			if (this.activeHandle === 'start') {
				const newEndPos = this.endHandlePos;
				const newStartPos = Math.min(
					newEndPos - this.effectiveMinScale,
					Math.max(0, this.startHandlePos + deltaPercent)
				);
				
				if (newStartPos !== this.startHandlePos) {
					this.startHandlePos = newStartPos;
					this.updateValues(newStartPos, newEndPos - newStartPos);
				}
			} else if (this.activeHandle === 'end') {
				const newStartPos = this.startHandlePos;
				const newEndPos = Math.max(
					newStartPos + this.effectiveMinScale,
					Math.min(1, this.endHandlePos + deltaPercent)
				);
				
				if (newEndPos !== this.endHandlePos) {
					this.endHandlePos = newEndPos;
					this.updateValues(newStartPos, newEndPos - newStartPos);
				}
			}
		},
		
		handleRangeDrag(event) {
			if (!this.isDraggingRange) return;
			if (!this.trackWidth) return;
			
			const deltaX = event.clientX - this.startDragX;
			const deltaPercent = deltaX / this.trackWidth;
			this.startDragX = event.clientX;
			
			let newStartPos = this.startHandlePos + deltaPercent;
			let newEndPos = this.endHandlePos + deltaPercent;
			
			// Ensure the range does not shrink and stays within bounds
			if (newStartPos < 0) {
				newStartPos = 0;
				newEndPos = this.endHandlePos - this.startHandlePos; // Maintain width
			} else if (newEndPos > 1) {
				newEndPos = 1;
				newStartPos = 1 - (this.endHandlePos - this.startHandlePos); // Maintain width
			}
			
			this.startHandlePos = newStartPos;
			this.endHandlePos = newEndPos;
			this.updateValues(newStartPos, newEndPos - newStartPos);
		},
		
		updateValues(newPosition, newScale) {
			this.internalUpdate = true; // Set flag before emitting
			this.position = newPosition;
			this.scale = newScale;
			
			this.$emit('range-update', {
				scale: this.scale,
				position: this.position,
			});
			
			// setTimeout(() => {
			// 	this.internalUpdate = false; // Reset flag after next tick
			// }, 100);

			this.$nextTick(() => {
				this.internalUpdate = false; // Reset flag after next tick
			});
		},
		
		endDrag() {
			this.isDraggingHandle = false;
			this.activeHandle = null;
			window.removeEventListener('mousemove', this.handleDrag);
			window.removeEventListener('mouseup', this.endDrag);
		},
		
		endRangeDrag() {
			this.isDraggingRange = false;
			window.removeEventListener('mousemove', this.handleRangeDrag);
			window.removeEventListener('mouseup', this.endRangeDrag);
		},
		
		updateContainerWidth() {
			if (this.$refs.container) {
				this.containerWidth = this.$refs.container.offsetWidth;
			}
		},

		handleScroll(event) {
			// Implementation of handleScroll method
		},
	},
	mounted() {
		this.updateContainerWidth();
		window.addEventListener('resize', this.updateContainerWidth);
		this.resizeObserver = new ResizeObserver(this.updateContainerWidth);
		this.resizeObserver.observe(this.$refs.container);
	},
	beforeUnmount() {
		window.removeEventListener('resize', this.updateContainerWidth);
		this.resizeObserver?.disconnect();
	}
};
</script>



<style lang="stylus" scoped>

.chart-range
	--barheight .2em
	--barheight 12px
	padding: 0
	margin .1em 0
	user-select: none
	filter var(--dropshadowfilter)

.range-bar
	margin 0
	position: relative
	height: var(--barheight)
	background-color: #fff
	border-radius: calc(var(--barheight) / 2)
	background-color: #eee

.range-indicator
	position: absolute
	z-index 1
	height: 100%
	border-radius: calc(var(--barheight) / 2)
	background var(--activecolorgreyopaque)
	cursor: grab
	&:active
		cursor: grabbing

.handle
	position: absolute
	z-index 2
	width: var(--barheight)
	height: var(--barheight)
	border-radius: 50%
	background-color: #888
	// background-color: #666
	top: 50%
	cursor: ew-resize
	&.start-handle
		transform: translate(0, -50%)
	&.end-handle
		transform: translate(-100%, -50%)

</style>
